import React, { type PropsWithChildren, createContext, use } from "react";

/**
 * Context value that includes both the rootTag and a provider detection flag
 */
interface CrossPlatformRootTagValue {
  rootTag: number;
  hasProvider: boolean;
}

/**
 * Cross-platform root tag context that safely handles web platform
 */
export const CrossPlatformRootTagContext = createContext<CrossPlatformRootTagValue>({
  rootTag: 0,
  hasProvider: false,
});

/**
 * Provides a safe fallback value for web (0) since react-native-web doesn't export RootTagContext
 * On native: extracts and provides the actual rootTag from React Native's RootTagContext
 */
export function CrossPlatformRootTagProvider({ children }: PropsWithChildren) {
  return (
    <CrossPlatformRootTagContext.Provider value={{ rootTag: 0, hasProvider: true }}>
      {children}
    </CrossPlatformRootTagContext.Provider>
  );
}

/**
 * Hook to get the root tag value that works across all platforms
 *
 * @returns The root tag number (0 on web, actual rootTag on native platforms)
 */
export function useCrossPlatformRootTag(): number {
  const { rootTag } = use(CrossPlatformRootTagContext);
  return rootTag;
}
